import { Section } from "@models/ISchema";

export type ScheduledBlocksState = "success" | "warning" | "error" | "neutral";

interface ScheduledBlocksBalance {
  assigned: number;
  planned?: number | null;
}

/**
 * Badge state for the section scheduled-blocks (UPD) indicator (CHS-2785 / EDH-014):
 * neutral when there is no planned value, success on an exact match, warning on a
 * deficit (assigned below planned), error on excess (assigned above planned).
 */
export const getScheduledBlocksState = ({
  assigned,
  planned,
}: ScheduledBlocksBalance): ScheduledBlocksState => {
  if (planned === null || planned === undefined) {
    return "neutral";
  }

  if (assigned === planned) {
    return "success";
  }

  return assigned > planned ? "error" : "warning";
};

interface BlockRangeOption {
  value?: string | null;
}

type EditedBlocks = {
  selected?: "hours" | "blocks" | null;
  startTime?: string | null;
  endTime?: string | null;
  blocks?: number | null;
} | null;

type SessionLike = {
  id?: string | null;
  blocksCount?: number | null;
  blocks?: EditedBlocks;
  intervals?: Array<{ checked?: boolean | null } | null> | null;
  session?: { blocksCount?: number | null } | null;
} | null;

type MergeableSection = {
  sessions?: SessionLike[] | null;
  unasignedSessions?: SessionLike[] | null;
} | null;

/**
 * Number of blocks spanned by an hours range, derived the same way `BlockRange`'s
 * `changeWithBlockRange` does: both the start and end times live in the ordered `end`
 * options list, and a session of N blocks ends at `end[indexStart + N]`. So the block
 * count is `indexEnd - indexStart`. Returns null when it can't be resolved.
 */
export const countBlocksInRange = (
  startTime?: string | null,
  endTime?: string | null,
  endOptions: BlockRangeOption[] = [],
): number | null => {
  if (!startTime || !endTime || !endOptions.length) {
    return null;
  }

  const indexStart = endOptions.findIndex(option => option?.value === startTime);
  const indexEnd = endOptions.findIndex(option => option?.value === endTime);

  if (indexStart === -1 || indexEnd === -1) {
    return null;
  }

  const count = indexEnd - indexStart;
  return count > 0 ? count : null;
};

/**
 * Resolves a single session's block count, accounting for in-editor changes:
 * - edited in "blocks" mode → the explicit block number;
 * - edited in "hours" mode → derived from the start/end range, so widening a session
 *   with "Mantener duración" unchecked is reflected live;
 * - otherwise → the session's own `blocksCount` (falling back to the wrapped original
 *   for edited sessions that did not change their duration).
 */
export const getSessionBlocks = (
  session: SessionLike,
  endOptions: BlockRangeOption[] = [],
): number => {
  const editedBlocks = session?.blocks;

  if (editedBlocks?.selected === "blocks") {
    return editedBlocks.blocks ?? 0;
  }

  if (editedBlocks?.selected === "hours") {
    const rangeCount = countBlocksInRange(editedBlocks.startTime, editedBlocks.endTime, endOptions);
    if (rangeCount !== null) {
      return rangeCount;
    }
  }

  return session?.blocksCount ?? session?.session?.blocksCount ?? 0;
};

/** Number of weeks a session is scheduled (checked intervals). */
export const getSessionWeekCount = (session: SessionLike): number =>
  (session?.intervals ?? []).filter(interval => interval?.checked).length;

/**
 * Total block-events for a session: blocks-per-week × scheduled weeks. Matches the
 * backend `Section::getTotalBlocks` (which counts `resultados` rows), so the badge, the
 * incidence message and the validation all report the same number (CHS-2785).
 */
export const getSessionTotalBlocks = (
  session: SessionLike,
  endOptions: BlockRangeOption[] = [],
): number => getSessionBlocks(session, endOptions) * getSessionWeekCount(session);

export const sumSessionTotalBlocks = (
  sessions: SessionLike[] = [],
  endOptions: BlockRangeOption[] = [],
): number =>
  sessions.reduce((total, session) => total + getSessionTotalBlocks(session, endOptions), 0);

/**
 * Total assigned blocks for a section reflecting the current in-editor changes: merges
 * saved edits over the section's sessions, drops deleted ones and adds created sessions,
 * then sums block-events. Shared by the edit and result sidebars so both report the same
 * number (CHS-2785).
 */
export const getSectionAssignedBlocks = (
  section: MergeableSection,
  savedSessions: SessionLike[] = [],
  createdSessions: SessionLike[] = [],
  sessionsToDelete: Record<string, unknown> = {},
  endOptions: BlockRangeOption[] = [],
): number => {
  const savedById: Record<string, SessionLike> = {};
  savedSessions.forEach(session => {
    if (session?.id) savedById[session.id] = session;
  });

  const existing = [...(section?.sessions ?? []), ...(section?.unasignedSessions ?? [])]
    .map(session => (session?.id && savedById[session.id] ? savedById[session.id] : session))
    .filter(session => !(session?.id && sessionsToDelete?.[session.id]));

  return (
    sumSessionTotalBlocks(existing, endOptions) + sumSessionTotalBlocks(createdSessions, endOptions)
  );
};

/**
 * Offering-level gating (design decision 3): the badge treatment is active only
 * when at least one section in the offering has a non-null planned value. Within
 * an active offering, a section whose own value is null still shows the neutral state.
 */
export const hasAnyScheduledBlocks = (
  sections: Array<Pick<Section, "courseComponent"> | null | undefined> = [],
): boolean => sections.some(section => section?.courseComponent?.scheduledBlocksCount != null);
